<!-- The activities dashboard (E4 RF-46 to RF-49). Hyper cards and the dashboard's KPI tiles; Material Design Icons throughout. -->
<div class="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
  <h4 class="header-title mb-0">{{ 'task.dashboard.title' | translate }}</h4>
  <!-- The window chooser: the selector pattern — a primary button carrying the choice, a mat-menu of options. -->
  <button class="btn trend-button btn-primary fw-bold mdi mdi-calendar-range dropdown-toggle" [matMenuTriggerFor]="windowMenu">
    {{ windowLabel | translate }}
  </button>
  <mat-menu #windowMenu="matMenu">
    <button mat-menu-item (click)="setWindow(7)">{{ 'task.dashboard.window7' | translate }}</button>
    <button mat-menu-item (click)="setWindow(30)">{{ 'task.dashboard.window30' | translate }}</button>
  </mat-menu>
</div>

@if (loading && !reading) {
  <content-loading-list />
} @else if (failed && !reading) {
  <div class="alert alert-danger py-2" role="alert">
    <i class="mdi mdi-alert-circle-outline me-1"></i>{{ 'task.dashboard.loadError' | translate }}
  </div>
} @else if (reading) {
  <!-- RF-46: the personal figures, on the dashboard's own tiles. -->
  <!-- Design (canvas "Painel"): one gutter between the tiles and the chart card — the tile drops its own card margin from 576px up. -->
  <div class="row mb-sm-3">
    <div class="col-md-6 col-xl-2">
      <card-graphic-small-legacy-component
        [label]="reading.today.concluded + ' / ' + reading.today.scheduled"
        [total]="'task.dashboard.todayProgress' | translate"
      />
    </div>
    <div class="col-md-6 col-xl-2">
      <!-- Pending includes the activities with no date; the caption says how many, since no day view lists them. -->
      <div [matTooltip]="reading.undated > 0 ? ('task.activity.noDateHint' | translate) : ''">
        <card-graphic-small-legacy-component
          [label]="reading.pending.toString()"
          [total]="(reading.undated > 0 ? 'task.dashboard.pendingUndated' : 'task.dashboard.pending') | translate: { count: reading.undated }"
        />
      </div>
    </div>
    <div class="col-md-6 col-xl-2">
      <card-graphic-small-legacy-component [label]="reading.overdue.toString()" [total]="'task.dashboard.overdue' | translate" />
    </div>
    <div class="col-md-6 col-xl-2">
      <card-graphic-small-legacy-component [label]="reading.concludedThisWeek.toString()" [total]="'task.dashboard.week' | translate" />
    </div>
    <div class="col-md-6 col-xl-2">
      <card-graphic-small-legacy-component [label]="reading.skippedNextSteps.toString()" [total]="'task.dashboard.skipped' | translate" />
    </div>
    <div class="col-md-6 col-xl-2">
      <!-- RF-47: the streak; "N+" at the 120-day bound. -->
      <div [matTooltip]="'task.dashboard.streakHint' | translate">
        <!-- Design: the tile reads "N dias" ("N+ dias" at the bound); the discipline table keeps the bare figure. -->
        <card-graphic-small-legacy-component
          [label]="'task.dashboard.streakDays' | translate: { days: streakLabel(reading.streak) }"
          [total]="'task.dashboard.streak' | translate"
        />
      </div>
    </div>
  </div>

  <!-- RF-46: conclusions per day of the window. -->
  <div class="card">
    <div class="d-flex flex-column card-header">
      <h3>{{ 'task.dashboard.perDayTitle' | translate }}</h3>
      <h5 class="text-muted fw-normal">{{ 'task.dashboard.perDaySubtitle' | translate }}</h5>
    </div>
    <div class="card-body pt-0 pb-3" #chartContainer>
      <ngx-charts-bar-vertical
        [view]="chartView"
        [results]="chartData"
        [scheme]="colorScheme"
        [gradient]="false"
        [xAxis]="true"
        [yAxis]="true"
        [showXAxisLabel]="true"
        [showYAxisLabel]="true"
        [xAxisLabel]="'task.dashboard.chartAxisDay' | translate"
        [yAxisLabel]="'task.dashboard.chartAxisCount' | translate"
      >
      </ngx-charts-bar-vertical>
    </div>
  </div>

  <!-- RF-48, RF-49: the team's discipline, supervisors and administrators only. -->
  @if (canReadDiscipline) {
    <div class="card">
      <div class="card-body table-responsive">
        <h4 class="header-title">{{ 'task.discipline.title' | translate }}</h4>
        <p class="text-muted mb-3">{{ 'task.discipline.subtitle' | translate }}</p>

        @if (disciplineFailed && !discipline) {
          <div class="alert alert-danger py-2 mb-0" role="alert">
            <i class="mdi mdi-alert-circle-outline me-1"></i>{{ 'task.discipline.loadError' | translate }}
          </div>
        } @else if (!discipline?.rows?.length) {
          <p class="text-muted mb-0">{{ 'task.discipline.empty' | translate }}</p>
        } @else {
          <table class="table table-hover table-centered mb-0">
            <thead>
              <tr>
                <th class="text-primary-emphasis">{{ 'task.discipline.columnPerson' | translate }}</th>
                <th class="text-primary-emphasis text-end">{{ 'task.discipline.columnScheduled' | translate }}</th>
                <th class="text-primary-emphasis text-end">{{ 'task.discipline.columnConcluded' | translate }}</th>
                <th class="text-primary-emphasis text-end">{{ 'task.discipline.columnOverdue' | translate }}</th>
                <th class="text-primary-emphasis text-end">{{ 'task.discipline.columnCompletion' | translate }}</th>
                <th class="text-primary-emphasis text-end">{{ 'task.discipline.columnPostponements' | translate }}</th>
                <th class="text-primary-emphasis text-end">{{ 'task.discipline.columnSkipped' | translate }}</th>
                <th class="text-primary-emphasis text-end">{{ 'task.discipline.columnOpenDeals' | translate }}</th>
                <th class="text-primary-emphasis text-end">{{ 'task.discipline.columnWithoutNext' | translate }}</th>
                <th class="text-primary-emphasis text-end">{{ 'task.discipline.columnStreak' | translate }}</th>
                <th class="text-primary-emphasis text-end">{{ 'task.discipline.columnPerDay' | translate }}</th>
                <th class="text-primary-emphasis text-end">{{ 'task.discipline.columnDuration' | translate }}</th>
              </tr>
            </thead>
            <tbody>
              @for (row of discipline.rows; track rowKey($index, row)) {
                <tr>
                  <td>
                    <div class="d-flex align-items-center gap-2">
                      <img class="avatar-xs rounded-circle" [src]="row.user.image || ''" [alt]="row.user.name" (error)="onImageError($event)" />
                      <span class="fw-bold">{{ row.user.name }}</span>
                    </div>
                  </td>
                  <td class="text-end">{{ row.scheduled }}</td>
                  <td class="text-end">{{ row.concluded }}</td>
                  <td class="text-end">
                    @if (row.overdue > 0) {
                      <span class="badge campaign-badge is-err">{{ row.overdue }}</span>
                    } @else {
                      0
                    }
                  </td>
                  <td class="text-end">{{ percent(row.completionRate) }}</td>
                  <td class="text-end">{{ row.postponements }} <small class="text-muted">({{ percent(row.postponementRate) }})</small></td>
                  <td class="text-end">{{ row.skippedNextSteps }}</td>
                  <td class="text-end">{{ row.openDeals }}</td>
                  <td class="text-end">
                    @if (row.openDealsWithoutNextAction > 0) {
                      <span class="badge campaign-badge is-warn">{{ row.openDealsWithoutNextAction }}</span>
                    } @else {
                      0
                    }
                  </td>
                  <td class="text-end">{{ streakLabel(row.streak) }}</td>
                  <td class="text-end">{{ numberOrDash(row.averageConclusionsPerActiveDay) }}</td>
                  <td class="text-end">
                    @if (row.averageDurationMinutes !== null) {
                      {{ 'task.activity.duration' | translate: { minutes: row.averageDurationMinutes } }}
                    } @else {
                      —
                    }
                  </td>
                </tr>
              }
            </tbody>
          </table>
        }
      </div>
    </div>
  }
}
